import { api } from '@/lib/api/client';
import { ApiError } from '@/lib/api/errors';
import type {
  XkEvent,
  XkEventGroup,
  XkEventStockStation,
  XkStation,
  XkStationSummary,
} from '@/lib/api/types';

/**
 * Backend list endpoints filter out `status='Deleted'` rows automatically,
 * but single-entity GETs (`/xk-event/:id`, etc.) do NOT. To keep soft-deleted
 * entities invisible everywhere, every `get*` below runs the response
 * through this guard.
 */
function assertNotDeleted<T extends { status?: string }>(entity: T, kind: string): T {
  if (entity?.status === 'Deleted') {
    throw new ApiError({
      message: `${kind} has been deleted`,
      status: 404,
      code: 'NOT_FOUND',
    });
  }
  return entity;
}

/**
 * Soft-delete convention used by all delete / remove methods:
 *
 * The backend `DELETE /resource/:id` endpoints route through `deleteMany`,
 * which calls `getOne` with the company filter applied. That blocks Agency
 * users from soft-deleting entities owned by their sub-brands (the brand's
 * `companyId` ≠ the agency user's `companyId`).
 *
 * To work around this WITHOUT a backend change, we issue `PUT /resource/:id`
 * with `{ status: 'Deleted' }`. The update path uses `repository.update(...)`
 * directly (no `getOne` guard), and `BaseService.applyFilters` already hides
 * rows where `status = 'Deleted'` from every list/findOne query — so the
 * entity disappears from listings just like a real delete would.
 *
 * If/when the backend's `getOne` learns to accept Agency users (e.g. via
 * `co_company_accesses`), these can flip back to real `DELETE` calls.
 */
export const eventsApi = {
  listGroups: (filter?: Record<string, unknown>) =>
    api.list<XkEventGroup>('/xk-event-group', { filter }),
  getGroup: (id: number) =>
    api.get<XkEventGroup>(`/xk-event-group/${id}`).then((g) => assertNotDeleted(g, 'Project')),
  createGroup: (input: Partial<XkEventGroup>) =>
    api.post<XkEventGroup>('/xk-event-group', input),
  updateGroup: (id: number, patch: Partial<XkEventGroup>) =>
    api.put<XkEventGroup>(`/xk-event-group/${id}`, patch),
  deleteGroup: (id: number) =>
    api.put<XkEventGroup>(`/xk-event-group/${id}`, { status: 'Deleted' }),

  listEvents: (filter?: Record<string, unknown>) =>
    api.list<XkEvent>('/xk-event', { filter }),
  getEvent: (id: number) =>
    api.get<XkEvent>(`/xk-event/${id}`).then((e) => assertNotDeleted(e, 'Event')),
  createEvent: (input: Partial<XkEvent>) => api.post<XkEvent>('/xk-event', input),
  /**
   * Batch-create N events under a project in a single call. The backend
   * auto-numbers names (Event1..EventN), spreads dates across the project
   * range, and gives each event one default station. Plan caps are enforced
   * line-by-line — the first overflow aborts with 409.
   */
  createEventsBatch: (input: {
    eventGroupId: number;
    count: number;
    companyId?: number;
    address?: string;
    sectionCount?: number;
  }) =>
    api.post<{ created: number; items: XkEvent[] }>('/xk-event/batch', input),
  /**
   * Advanced create: build the event (or N events) together with their
   * stations and split the stock across them, in one call.
   *
   * The backend validates the WHOLE thing — plan caps and brand inventory for
   * every event at once — before writing anything, so a request that doesn't
   * fit fails with nothing created. Doing this as a loop of client-side calls
   * would leave half a tree behind on the first rejection.
   */
  scaffold: (input: {
    eventGroupId: number;
    companyId?: number;
    /** One event with its own name and dates. */
    event?: { name: string; address?: string; startDate?: string; endDate?: string };
    /** Or N auto-numbered events spread across the project range. */
    events?: number;
    /**
     * Venue shared by every event the scaffold creates.
     *
     * A four-stop tour at the same venue had to retype the address four times,
     * afterwards, event by event. It is the part of a scaffold that repeats
     * most and varies least.
     */
    address?: string;
    latitude?: number | null;
    longitude?: number | null;
    placeId?: string | null;
    stationsPerEvent?: number;
    /** Units **per event**; split evenly across that event's stations. */
    stock?: Array<{ stockId: number; quantityPerEvent: number }>;
  }) =>
    api.post<{
      events: XkEvent[];
      stations: number;
      allocations: number;
      notes: string[];
    }>('/xk-event/scaffold', input),
  updateEvent: (id: number, patch: Partial<XkEvent>) =>
    api.put<XkEvent>(`/xk-event/${id}`, patch),
  deleteEvent: (id: number) =>
    api.put<XkEvent>(`/xk-event/${id}`, { status: 'Deleted' }),

  listStations: (filter?: Record<string, unknown>) =>
    api.list<XkStation>('/xk-station', { filter }),
  getStation: (id: number) =>
    api.get<XkStation>(`/xk-station/${id}`).then((s) => assertNotDeleted(s, 'Station')),
  createStation: (input: Partial<XkStation>) =>
    api.post<XkStation>('/xk-station', input),
  updateStation: (id: number, patch: Partial<XkStation>) =>
    api.put<XkStation>(`/xk-station/${id}`, patch),
  /** 094 — permitir (o no) que el operador edite; genera el código al activar. */
  setOperatorEdit: (id: number, enabled: boolean) =>
    api.patch<XkStation>(`/xk-station/${id}/operator-edit`, { enabled }),
  rotateOperatorCode: (id: number) =>
    api.post<XkStation>(`/xk-station/${id}/operator-code/rotate`, {}),
  /**
   * Desde la sesión de operador: el código devuelve un JWT con `edit: true`.
   * Diez intentos por minuto en el servidor; el 429 se enseña como tal.
   */
  operatorUnlock: (stationId: number, code: string) =>
    api.post<{ access_token: string; expiresInSec: number }>(
      `/xk-station/${stationId}/operator/unlock`,
      { code },
    ),
  /** La versión recortada del PUT de asignación que puede usar un operador. */
  operatorPatchAllocation: (
    stationId: number,
    allocId: number,
    patch: {
      available?: number;
      prizeMode?: 'weight' | 'cooldown';
      rating?: number;
      cooldownMinutes?: number;
      hiddenFromGames?: boolean;
    },
  ) =>
    api.patch<XkEventStockStation>(
      `/xk-station/${stationId}/operator/allocations/${allocId}`,
      patch,
    ),
  deleteStation: (id: number) =>
    api.put<XkStation>(`/xk-station/${id}`, { status: 'Deleted' }),
  /**
   * Clone an existing station (with all its stock allocations) into another
   * event. Backend resets `available = quantity` on each clone. Returns the
   * newly created station.
   */
  cloneStation: (input: {
    id: number;
    surveyId: number;
    eventSelectId: number;
    name: string;
  }) => api.post<XkStation>('/xk-station/clone', input),

  /**
   * Composite station-detail payload for the Station Detail page.
   * Backed by `GET /xk-station/:id/summary` — event + survey + inventory
   * with `used = quantity - available` derived, plus generic response
   * analytics (time series, top answers, last 24h/7d) from the shared
   * TouchpointInsightsService kernel.
   */
  getStationSummary: (id: number) =>
    api.get<XkStationSummary>(`/xk-station/${id}/summary`),

};
